import {
  type Application,
  type Container,
  type FederatedPointerEvent,
  Graphics,
  Point,
  type PointData,
} from 'pixi.js';

import { ciede2000 } from './color-diff';
import { bgColor, blurrerAlpha, blurrerWidth, snaplineAlpha, snaplineSensitivityPx } from './constants';
import {
  type DragObject,
  type DragSprite,
  DragState,
  type Draggable,
  type Point as PointType,
} from './types';

/** Return a point ensuring the x and y values are between the max and min */
export const clampPosition = (min: PointData, max: PointData, point: PointData): PointData => {
  const x = Math.max(min.x, Math.min(max.x, point.x));
  const y = Math.max(min.y, Math.min(max.y, point.y));
  return { x, y };
};

/** Return the statistical mode of an array of numbers */
const mode = (numbers: number[]): number | undefined => {
  const counts: Record<number, number> = {};

  numbers.forEach((num) => {
    if (!(num in counts)) {
      counts[num] = 0;
    }

    counts[num]++;
  });

  let bestElement;
  let bestCount = 0;

  Object.entries(counts).forEach(([num, count]) => {
    if (count > bestCount) {
      bestElement = num;
      bestCount = count;
    }
  });

  if (bestElement) {
    return parseInt(bestElement);
  }
};

/**  */
export const calculateColorDeltas = (
  center: number,
  values: number[],
  thresh: number,
): { center: number; values: number[] } => {
  const newValues: number[] = [];
  for (const v of values) {
    const delta = ciede2000(center, v);
    if (delta < thresh) {
      // it;s cloise
      newValues.push(v);
    }
  }

  // calc center as mode of new values

  return { center: mode(newValues) || center, values: newValues };
};

/** Converts a number to a hex color string */
export const numberToColorHex = (n: number): string => {
  return '#' + n.toString(16).padStart(6, '0');
};
/** convert a hex color string to a number. '#' is optional. */
export const colorHexToNumber = (hex: string): number => {
  if (hex.startsWith('#')) {
    return parseInt(hex.slice(1), 16);
  } else {
    return parseInt(hex, 16);
  }
};
/** Converts a color number to an RGB color */
export const colorNumberToRGB = (n: number): { R: number; G: number; B: number } => {
  const hex = numberToColorHex(n).replace('#', '');

  return {
    R: colorHexToNumber(hex.substring(0, 2)),
    G: colorHexToNumber(hex.substring(2, 4)),
    B: colorHexToNumber(hex.substring(4, 6)),
  };
};

interface ValueCount {
  color: number;
  count: number;
}

/** Calculate counts of each value in the array */
export const valueCounts = (arr: number[]): { mode: ValueCount; valueCounts: ValueCount[] } => {
  const result: ValueCount[] = [];
  const mode = {
    color: 0,
    count: 0,
  };
  for (let i = 0; i < arr.length; i++) {
    const color = arr[i];
    if (!color) continue;
    const current = result.find((_) => _.color === color);

    if (current) {
      current.count++;
      if (current.count > mode.count) {
        mode.color = color;
        mode.count = current.count;
      }
    } else {
      result.push({ color, count: 1 });
    }
  }

  return { mode, valueCounts: result };
};

// Type for event listener function in Pixi v8
type PixiEventListener = (e: FederatedPointerEvent) => void;

/** Create a handler for starting to drag a PIXI shape.
 * Enables the draging effect for the shape.
 * Should be called when you want to start dragging the object (e.g. mouse down)
 */
export const createOnDragStart = (
  shape: DragObject | DragSprite,
  canDrag: () => boolean,
): PixiEventListener => {
  return (e: FederatedPointerEvent) => {
    if (canDrag()) {
      const obj = e.currentTarget as DragObject | DragSprite;
      obj.dragData = e;
      obj.dragging = DragState.DragStarted;
      obj.dragPointerStart = obj.parent ? e.getLocalPosition(obj.parent) : { x: e.global.x, y: e.global.y };
      obj.dragObjStart = new Point();
      obj.dragObjStart.copyFrom(obj.position);
      obj.dragGlobalStart = new Point();
      obj.dragGlobalStart.copyFrom(e.global);
      obj.cursor = 'grabbing';
    }
  };
};

/** Create a handler for ending the dragging of a PIXI shape.
 * Disables moving the the shape when called.
 * Should be called when you want the dragging to end (e.g. on mouse up)
 */

export const createOnDragEnd = (
  obj: Draggable,
  app: Application,
  onDragged?: (p: Point) => void,
): PixiEventListener => {
  return (e: FederatedPointerEvent) => {
    const obj = e.currentTarget as DragObject | DragSprite;
    if (!obj.dragging) return;

    obj.dragging = DragState.NotDragging;
    obj.cursor = 'grab'; // enable grab cursor again

    const data = obj.dragData; // it can be different pointer!
    if (data) {
      const p = data.getLocalPosition(obj.parent as Container);
      onDragged && onDragged(p as Point);
    }
    // set the interaction data to null
    // obj.dragData = null
  };
};

/** Create a handler for when the the PIXI object moves.
 * Actually changes the position of the shape.
 * Should be called on mouse position changes
 */
export const createOnDragMove = (
  obj: Draggable,
  onDragged?: () => void,
  calcMin?: () => PointData,
  calcMax?: () => PointData,
): PixiEventListener => {
  return (e: FederatedPointerEvent) => {
    // this runs all the time, but need to know whether we are actually dragging
    const obj = e.currentTarget as DragObject | DragSprite;
    if (!obj || !obj.dragGlobalStart || !obj.dragObjStart || !obj.dragPointerStart) return;
    if (!obj.dragging) return;
    const data = obj.dragData; // it can be different pointer!
    if (!data) return;
    // check if we've just started dragging and the drag start handler has been called
    if (obj.dragging === DragState.DragStarted) {
      if (
        Math.abs(data.global.x - obj.dragGlobalStart.x) + Math.abs(data.global.y - obj.dragGlobalStart.y) >=
        3
      ) {
        // DRAG
        obj.dragging = DragState.Dragging;
      }
    }
    if (obj.dragging === DragState.Dragging) {
      const dragPointerEnd = data.getLocalPosition(obj.parent as Container);
      // Actually change the position of the shape.
      const min = calcMin ? calcMin() : { x: 0, y: 0 };
      const max = calcMax ? calcMax() : { x: 1000, y: 1000 };
      const nextX = obj.dragObjStart.x + (dragPointerEnd.x - obj.dragPointerStart.x);
      const nextY = obj.dragObjStart.y + (dragPointerEnd.y - obj.dragPointerStart.y);
      const newPosition = clampPosition(min, max, { x: nextX, y: nextY });

      obj.position.set(newPosition.x, newPosition.y);

      typeof onDragged === 'function' && onDragged();
    }
  };
};

interface ViewfinderConfig {
  lineWidth: number;
  width: number;
  height: number;
  lineColor?: number;
  editorDimensions: PointData;
}

interface Viewfinder {
  rectangle: Graphics;
  blurrer: Graphics;
}

/** Make a viewfinder shape for the editor
 * A viewfinder is the shape that defined the crop area.
 */
export const makeViewfinder = ({
  lineWidth,
  width,
  height,
  lineColor = bgColor,
  editorDimensions,
}: ViewfinderConfig): Viewfinder => {
  // Pixi v8: build shape then stroke/fill
  const rectangle = new Graphics()
    .rect(0, 0, width, height)
    .fill({ color: 0, alpha: 0 })
    .stroke({ width: lineWidth, color: lineColor });

  rectangle.position.x = (editorDimensions.x - width) / 2;
  rectangle.position.y = (editorDimensions.y - height) / 2;
  rectangle.label = 'viewfinder';

  // add a blurrer - Pixi v8: build shape then stroke
  const blurrer = new Graphics()
    .rect(0, 0, width + blurrerWidth, height + blurrerWidth)
    .stroke({ width: blurrerWidth, color: 0x000000, alpha: blurrerAlpha });
  blurrer.position.set(rectangle.position.x - blurrerWidth / 2, rectangle.position.y - blurrerWidth / 2);

  return {
    rectangle,
    blurrer,
  };
};

export const isPositionSnappable = (objectPosition: number, snapPosition: number): boolean => {
  return Math.abs(objectPosition - snapPosition) < snaplineSensitivityPx;
};

/** A function that animates a snapline, based on the position of the image relative to the line */
export const animateSnapline = (
  snapline: Graphics,
  canDrag: () => boolean,
  isDragging: boolean,
  center: number,
  position: number,
  delta: number,
): boolean => {
  const snapOn = isPositionSnappable(center, position);
  if (canDrag()) {
    if (snapOn) {
      // we want this to decay
      if (snapline.alpha > 0 && isDragging) {
        snapline.alpha = snaplineAlpha.max;
      } else if (snapline.alpha < snaplineAlpha.min + snaplineAlpha.step) {
        snapline.alpha = snaplineAlpha.min;
      } else {
        // add a spring constant here.
        const alphaDelta = snapline.alpha - snaplineAlpha.min;
        snapline.alpha -= snaplineAlpha.step * snaplineAlpha.spring * alphaDelta * delta;
      }
    } else {
      snapline.alpha = 0;
    }
  } else {
    snapline.alpha = 0;
  }
  return snapOn;
};

interface PointerState {
  evCache: PointerEvent[];
  prevDiff: number;
}

const removeEvent = (ev: PointerEvent, state: PointerState) => {
  // Remove this event from the target's cache
  for (let i = 0; i < state.evCache.length; i++) {
    if (state.evCache[i].pointerId === ev.pointerId) {
      state.evCache.splice(i, 1);
      break;
    }
  }
};

/** The pointerdown event signals the start of a touch interaction.
 *  This event is cached to support 2-finger gestures
 */
export const pointerDownHandler = (ev: PointerEvent, state: PointerState): void => {
  state.evCache.push(ev);
};

/**  This function implements a 2-pointer horizontal pinch/zoom gesture.
    
    If the distance between the two pointers has increased (zoom in),
    the target element's background is changed to "pink" and if the
    distance is decreasing (zoom out), the color is changed to "lightblue".

    Find this event in the cache and update its record with this event
*/
export const pointerMoveHandler = (
  ev: PointerEvent,
  state: PointerState,
  zoom: number,
  setZoom: (_: number) => void,
): void => {
  for (let i = 0; i < state.evCache.length; i++) {
    if (ev.pointerId === state.evCache[i].pointerId) {
      state.evCache[i] = ev;
      break;
    }
  }

  // If two pointers are down, check for pinch gestures
  if (state.evCache.length === 2) {
    // Calculate the distance between the two pointers
    const curDiff = Math.abs(state.evCache[0].clientX - state.evCache[1].clientX);

    if (state.prevDiff > 0) {
      // delta = the change in distance between the two pointers.
      // delta > 0 -> The distance between the two pointers has increased -> zoom out
      // delta < 0 -> The distance between the two pointers has decreased -> zoom in
      const delta = curDiff - state.prevDiff;
      setZoom(zoom + delta);
    }

    // Cache the distance for the next move event
    state.prevDiff = curDiff;
  }

  ev.preventDefault(); // dont let the browser move etc.
};

/** Reset the state when the pointer is removed from the screen  */
export const pointerUpHandler = (ev: PointerEvent, state: PointerState): void => {
  // Remove this pointer from the cache and reset the target's
  // background and border
  removeEvent(ev, state);

  // If the number of pointers down is less than two then reset diff tracker
  if (state.evCache.length < 2) {
    state.prevDiff = -1;
  }
};

/** Implements a simple spring equation for springy animations etc. */
export function spring(springConstant: number, current: number, desired: number): number {
  const delta = current - desired;
  return current - delta * springConstant;
}

/** Takes a point [-1, 1] and the editor dimensions and returns the internal (denormalized) position */
export function denormalizePosition(point: PointType, dimensions: PointType): PointType {
  const center = { x: dimensions.x / 2, y: dimensions.y / 2 };
  const result = {
    x: center.x + (dimensions.x * point.x) / 2,
    y: center.y + (dimensions.y * point.y) / 2,
  };
  return result;
}

/** Takes a point and editor dimensions and returns a normalized [-1,1] position */
export function normalizePosition(point: PointType, dimensions: PointType): PointType {
  const center = { x: dimensions.x / 2, y: dimensions.y / 2 };
  const result = {
    x: (2 * (point.x - center.x)) / dimensions.x,
    y: (2 * (point.y - center.y)) / dimensions.y,
  };
  return result;
}
